import { Controller } from '@hotwired/stimulus'
import { fetchTurbo } from '../utils/fetch-turbo'

// Перетаскивание тренировок в календаре кабинета тренера
// (coach_schedules/_calendar): блок хватают мышкой и бросают на другой день
// и время. Пока тянут, в колонке под курсором рисуется «тень» с новым
// временем; время привязано к шагу 15 минут, как в календаре CRM.
//
// Только мышь: на тач-экране тот же жест прокручивает календарь вбок, и
// перенос пальцем мешал бы листать неделю. Там тренировку по-прежнему
// открывают тапом.
//
// Проверки (прошлое, пересечения) — на сервере; он отвечает turbo-stream
// «visit», и страница перезагружается с тостом об успехе или ошибке.
const SNAP_MINUTES = 15
// Меньше — это клик (открыть окно тренировки), а не перенос.
const DRAG_THRESHOLD_PX = 5

export default class CoachCalendarController extends Controller {
  static targets = ['column', 'event']
  static values = {
    hourPx: Number,
    firstHour: Number,
    lastHour: Number,
    moveUrl: String,
    today: String,
    confirmTitle: String,
    confirmMessage: String,
    confirmOk: String
  }

  disconnect() {
    this.cleanup()
  }

  start(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return
    const block = event.currentTarget
    if (block.dataset.movable !== 'true') return

    const rect = block.getBoundingClientRect()
    this.drag = {
      block,
      startX: event.clientX,
      startY: event.clientY,
      // Где внутри блока взяли: тень должна ехать за курсором, не прыгая
      // верхним краем к указателю.
      grabOffset: event.clientY - rect.top,
      duration: Number(block.dataset.duration),
      active: false,
      target: null
    }
    this.onMove = this.move.bind(this)
    this.onUp = this.finish.bind(this)
    this.onKey = (keyEvent) => {
      if (keyEvent.key === 'Escape') this.cleanup()
    }
    document.addEventListener('pointermove', this.onMove)
    document.addEventListener('pointerup', this.onUp)
    document.addEventListener('keydown', this.onKey)
  }

  move(event) {
    const drag = this.drag
    if (!drag) return

    if (!drag.active) {
      const distance = Math.hypot(
        event.clientX - drag.startX,
        event.clientY - drag.startY
      )
      if (distance < DRAG_THRESHOLD_PX) return
      drag.active = true
      drag.block.classList.add('opacity-40')
      document.body.classList.add('cursor-grabbing', 'select-none')
    }
    event.preventDefault()

    const column = this.columnTargets.find((col) => {
      const rect = col.getBoundingClientRect()
      return event.clientX >= rect.left && event.clientX < rect.right
    })
    if (!column) {
      this.hideShadow()
      drag.target = null
      return
    }

    const rect = column.getBoundingClientRect()
    const top = event.clientY - rect.top - drag.grabOffset
    const maxMinutes = (this.lastHourValue - this.firstHourValue) * 60
    const raw = (top / this.hourPxValue) * 60
    const offset = Math.min(
      Math.max(Math.round(raw / SNAP_MINUTES) * SNAP_MINUTES, 0),
      Math.max(maxMinutes - SNAP_MINUTES, 0)
    )
    const minutes = this.firstHourValue * 60 + offset
    const valid = column.dataset.day >= this.todayValue

    drag.target = valid ? { day: column.dataset.day, minutes } : null
    this.showShadow(column, offset, minutes, valid)
  }

  async finish() {
    const drag = this.drag
    document.removeEventListener('pointermove', this.onMove)
    document.removeEventListener('pointerup', this.onUp)
    if (!drag || !drag.active) {
      this.cleanup()
      return
    }

    // Клик после перетаскивания открыл бы окно тренировки — гасим его.
    this.suppressClick = true
    setTimeout(() => (this.suppressClick = false), 0)

    const target = drag.target
    const unchanged =
      target &&
      target.day === drag.block.dataset.day &&
      target.minutes === Number(drag.block.dataset.minutes)
    if (!target || unchanged) {
      this.cleanup()
      return
    }

    // На тренировку записаны клиенты — они переедут вместе с ней, поэтому
    // переспрашиваем тренера.
    if (Number(drag.block.dataset.booked) > 0) {
      const confirmed = await this.confirm()
      if (!confirmed) {
        this.cleanup()
        return
      }
    }

    this.shadow?.classList.add('animate-pulse')
    const url = this.moveUrlValue.replace('__ID__', drag.block.dataset.id)
    try {
      const response = await fetchTurbo(url, {
        method: 'PATCH',
        body: JSON.stringify({ date: target.day, minutes: target.minutes })
      })
      // Вместо turbo-stream может прийти HTML-страница ошибки (истёкший
      // CSRF-токен, 500) — Turbo её не отрисует, и тень, полупрозрачный
      // блок и курсор так и остались бы висеть.
      const type = response.headers.get('Content-Type') || ''
      if (!type.includes('text/vnd.turbo-stream.html')) {
        this.cleanup()
        return
      }
      window.Turbo?.renderStreamMessage(await response.text())
    } catch {
      this.cleanup()
    }
  }

  // Ссылку браузер сам умеет тащить (drag-and-drop адреса) — это перебило
  // бы наш перенос.
  preventNative(event) {
    event.preventDefault()
  }

  // Клик по блоку без переноса открывает окно тренировки как обычно.
  click(event) {
    if (!this.suppressClick) return
    event.preventDefault()
    event.stopImmediatePropagation()
  }

  showShadow(column, offset, minutes, valid) {
    if (!this.shadow) {
      this.shadow = document.createElement('div')
      this.shadow.className =
        'pointer-events-none absolute inset-x-0.5 z-20 rounded-xs border-2 border-dashed px-1.5 py-1 text-xs font-semibold'
    }
    if (this.shadow.parentElement !== column) column.appendChild(this.shadow)

    // Как у блока тренировки (coach_calendar_style): тень через полночь
    // обрезается по низу колонки.
    const top = (offset / 60) * this.hourPxValue
    const bottom = (this.lastHourValue - this.firstHourValue) * this.hourPxValue
    const height = Math.min(
      Math.max((this.drag.duration / 60) * this.hourPxValue, 24),
      bottom - top
    )
    this.shadow.style.top = `${top}px`
    this.shadow.style.height = `${height}px`
    this.shadow.classList.toggle('border-text-blue', valid)
    this.shadow.classList.toggle('bg-bg-blue-subtle', valid)
    this.shadow.classList.toggle('border-text-error', !valid)
    this.shadow.textContent = valid ? this.formatTime(minutes) : '×'
  }

  hideShadow() {
    this.shadow?.remove()
  }

  formatTime(minutes) {
    const pad = (n) => String(n).padStart(2, '0')
    return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`
  }

  // Общая модалка подтверждения сайта (#tournament-confirm), как у
  // confirm-form, только без формы: ответ приходит промисом.
  confirm() {
    const modal = document.getElementById('tournament-confirm')
    // Модалка есть на странице расписания всегда; без неё переносить
    // тренировку с клиентами молча не будем.
    if (!modal) return Promise.resolve(false)

    return new Promise((resolve) => {
      modal.querySelector('[data-confirm-title]').textContent =
        this.confirmTitleValue
      modal.querySelector('[data-confirm-message]').textContent =
        this.confirmMessageValue
      const okButton = modal.querySelector('[data-confirm-ok]')
      okButton.textContent = this.confirmOkValue

      const abort = new AbortController()
      const close = (answer) => {
        modal.classList.remove('is-active')
        abort.abort()
        resolve(answer)
      }
      okButton.addEventListener('click', () => close(true), {
        signal: abort.signal
      })
      modal.querySelectorAll('[data-confirm-cancel]').forEach((el) => {
        el.addEventListener('click', () => close(false), {
          signal: abort.signal
        })
      })
      document.addEventListener(
        'keydown',
        (keyEvent) => {
          if (keyEvent.key === 'Escape') close(false)
        },
        { signal: abort.signal }
      )
      modal.classList.add('is-active')
      okButton.focus()
    })
  }

  cleanup() {
    document.removeEventListener('pointermove', this.onMove)
    document.removeEventListener('pointerup', this.onUp)
    document.removeEventListener('keydown', this.onKey)
    document.body.classList.remove('cursor-grabbing', 'select-none')
    this.drag?.block.classList.remove('opacity-40')
    this.hideShadow()
    this.drag = null
  }
}
